import { useState, useEffect } from "react";
import axios from "axios";
import { Mail, FileText, Lock } from "lucide-react";
import { FaLinkedinIn, FaInstagram } from "react-icons/fa";
import { Link } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LinkButton = ({ icon: Icon, title, href, onClick, isVisible = true }) => {
  if (!isVisible) return null;
  
  const ButtonContent = () => (
    <>
      <div className="link-button-icon-small">
        <Icon className="w-3 h-3 text-white" />
      </div>
      <div className="link-button-content-small">
        <span className="link-button-title-small">{title}</span>
      </div>
    </>
  );

  if (onClick) {
    return (
      <button onClick={onClick} className="link-button-small" data-testid={`btn-${title.toLowerCase().replace(/\s/g, '-')}`}>
        <ButtonContent />
      </button>
    );
  }

  return (
    <a href={href} target={href?.startsWith('mailto') ? '_self' : '_blank'} rel="noopener noreferrer" className="link-button-small" data-testid={`btn-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <ButtonContent />
    </a>
  );
};

const HomePage = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`);
        setSettings(response.data);
      } catch (error) {
        console.error("Error fetching settings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E8EBED] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#183153] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const handleResumeClick = () => {
    if (settings?.resume_url) {
      window.open(settings.resume_url, '_blank');
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#E8EBED' }} data-testid="homepage">
      
      {/* Top Banner */}
      {settings?.show_banner_top && settings?.banner_top && (
        <div className="w-full flex justify-center py-2 bg-white/50" data-testid="banner-top">
          <div dangerouslySetInnerHTML={{ __html: settings.banner_top }} />
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12 md:py-24">
        <div className="w-full max-w-2xl space-y-10">
          {/* Logo */}
          <div className="logo-container flex justify-center animate-fadeIn" data-testid="logo-container">
            <img 
              src={settings?.logo_url || "https://customer-assets.emergentagent.com/job_rudy-showcase/artifacts/acb2ywvs_rudy_bravo_logo_white_bg.png"} 
              alt="Rudy Bravo"
              className="h-48 md:h-64 lg:h-72 w-auto object-contain"
              data-testid="logo-image"
            />
          </div>

          {/* Headline & Subheadline */}
          {settings?.headline && (
            <div className="text-center animate-fadeIn animate-delay-100">
              <h1 className="font-display text-3xl md:text-4xl text-[#0F2F4F] tracking-tight">
                {settings.headline}
              </h1>
            </div>
          )}
          {settings?.subheadline && (
            <div className="text-center animate-fadeIn animate-delay-200">
              <p className="text-[#475569] text-base md:text-lg leading-relaxed">
                {settings.subheadline}
              </p>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 pt-6 max-w-lg mx-auto animate-fadeIn animate-delay-300">
            <LinkButton
              icon={Mail}
              title="Email Me"
              href={settings?.email ? `mailto:${settings.email}` : null}
              isVisible={settings?.show_email && settings?.email}
            />
            <LinkButton
              icon={FaLinkedinIn}
              title="LinkedIn"
              href={settings?.linkedin_url}
              isVisible={settings?.show_linkedin && settings?.linkedin_url}
            />
            <LinkButton
              icon={FaInstagram}
              title="Instagram"
              href={settings?.instagram_url}
              isVisible={settings?.show_instagram && settings?.instagram_url}
            />
            <LinkButton
              icon={FileText}
              title="Resume"
              onClick={handleResumeClick}
              isVisible={settings?.show_resume && settings?.resume_url}
            />
          </div>
        </div>
      </main>

      {/* Bottom Banner */}
      {settings?.show_banner_bottom && settings?.banner_bottom && (
        <div className="w-full flex justify-center py-2 bg-white/50" data-testid="banner-bottom">
          <div dangerouslySetInnerHTML={{ __html: settings.banner_bottom }} />
        </div>
      )}

      {/* Footer */}
      <footer className="py-6 px-6 border-t border-[#E2E8F0]" data-testid="footer">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <p className="text-sm text-[#94A3B8]">
            © {new Date().getFullYear()} Rudy Bravo
          </p>
          <Link to="/admin" className="admin-link text-[#94A3B8] hover:text-[#183153] transition-colors" data-testid="admin-link">
            <Lock className="w-4 h-4" />
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
